import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getImageDimensions } from "@react/foundation/browser";

import type { MediaAssetVisibility } from "@apis/media/models";

import { useAuth } from "../../../shared/contexts/auth/useAuth.ts";
import { useQueries } from "../../../shared/hooks/useQueries.ts";

interface MediaUploadInput {
  readonly file: File;
  readonly visibility: MediaAssetVisibility;
}

export function useMediaUpload(): UseMutationResult<
  void,
  Error,
  MediaUploadInput
> {
  const { api } = useAuth();

  const queryClient = useQueryClient();

  const { getMediaPreviewsQueryOptions } = useQueries();

  return useMutation({
    mutationFn: async ({ file, visibility }: MediaUploadInput) => {
      const { height, width } = await getImageDimensions(file);
      const item = await api.playground.createMediaUploadUrl({
        contentType: file.type,
        fileName: file.name,
        height,
        visibility,
        width,
      });

      const response = await fetch(item.uploadUrl, {
        body: file,
        method: "PUT",
      });

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}.`,
        );
      }

      await api.playground.finalizeMediaUpload({ id: item.mediaAsset.id });
    },
    onSuccess: async () => {
      const mediaPreviewsQueryOptions = getMediaPreviewsQueryOptions({});

      await queryClient.invalidateQueries({
        queryKey: mediaPreviewsQueryOptions.groupQueryKey,
      });
    },
  });
}
